/**
 * useCreateActionFields — shared edit-state logic for a create action's
 * schema-driven fields (deal name, amount, close date, …).
 *
 * Extracted from CreateActionEditForm so it can be reused by
 * MergedCreateActionCard, which needs to compose the primary entity's fields
 * with editable association targets under a single combined save. The hook owns
 * the values/schema/dirty computation and knows how to build the updated
 * ObjectCreatedEntityPayload; it does NOT register anything into the accordion
 * — the consumer decides how the save is wired.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeCreateConfig } from '@/lib/crm/record/registry/entityTypeRegistry';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { extractProperties, wrapProperties } from './CreateActionCard.helpers';

/** Seed a values record from the original flat properties for the given keys. */
function seedValues(
  fieldKeys: string[],
  originalFlat: Record<string, unknown>
): Record<string, unknown> {
  const init: Record<string, unknown> = {};
  for (const key of fieldKeys) {
    init[key] = originalFlat[key] ?? null;
  }
  return init;
}

export interface UseCreateActionFieldsResult {
  entityType: string;
  /** Ordered field keys to render (createConfig order + extra payload props). */
  fieldKeys: string[];
  /** Current (possibly edited) values keyed by property name. */
  values: Record<string, unknown>;
  /** Property schema map for choosing the right input widget. */
  schema: PropertySchemaMap;
  /** Update a single field value. */
  handleChange: (key: string, value: unknown) => void;
  /** True when any field differs from the original payload. */
  isDirty: boolean;
  /** Build the updated ObjectCreatedEntityPayload from the current values. */
  buildPayload: () => ObjectCreatedEntityPayload;
  /** Reset all fields back to the original payload values. */
  reset: () => void;
}

export function useCreateActionFields(
  action: ProposedAction<ObjectCreatedEntityPayload>,
  orgId?: number
): UseCreateActionFieldsResult {
  const { entity_type_name } = action.payload;

  const originalFlat = useMemo(
    () => extractProperties(action.payload.payload.properties),
    [action.payload.payload.properties]
  );

  // Fetch property schemas so we can render the correct input for each field.
  const { schema } = usePropertySchema(orgId ?? '', entity_type_name);

  // Ordered field list: createConfig properties present in the payload first
  // (same order as the CRM create modal), then any remaining payload props.
  const fieldKeys = useMemo(() => {
    const createConfig = getEntityTypeCreateConfig(entity_type_name);
    const payloadKeys = Object.keys(originalFlat);

    if (!createConfig) {
      return payloadKeys;
    }

    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const key of createConfig.createProperties) {
      if (key in originalFlat) {
        ordered.push(key);
        seen.add(key);
      }
    }
    for (const key of payloadKeys) {
      if (!seen.has(key)) {
        ordered.push(key);
      }
    }
    return ordered;
  }, [entity_type_name, originalFlat]);

  // Values stored as unknown so currency objects, dates, etc. keep native types.
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    seedValues(fieldKeys, originalFlat)
  );

  // Re-seed when the source payload changes (after a save or external update).
  useEffect(() => {
    setValues(seedValues(fieldKeys, originalFlat));
  }, [originalFlat, fieldKeys]);

  const isDirty = useMemo(
    () =>
      fieldKeys.some(key => {
        const original = originalFlat[key] ?? null;
        const edited = values[key] ?? null;
        return JSON.stringify(original) !== JSON.stringify(edited);
      }),
    [fieldKeys, originalFlat, values]
  );

  const handleChange = useCallback((key: string, value: unknown) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const buildPayload = useCallback(
    (): ObjectCreatedEntityPayload => ({
      ...action.payload,
      payload: {
        ...action.payload.payload,
        properties: wrapProperties(values),
      },
    }),
    [action.payload, values]
  );

  const reset = useCallback(() => {
    setValues(seedValues(fieldKeys, originalFlat));
  }, [fieldKeys, originalFlat]);

  return {
    entityType: entity_type_name,
    fieldKeys,
    values,
    schema,
    handleChange,
    isDirty,
    buildPayload,
    reset,
  };
}
